import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import {
  Lock, Play, CheckCircle, ChevronRight, ArrowLeft,
  Loader2, Calendar, Award, BarChart2, X, User, Mail, Phone,
  Star, MessageSquare, ExternalLink, Gift, ThumbsUp, Key, Copy
} from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

// ─── Helper: build token key from slug ───────────────────────────────────────
const tokenKey  = (slug) => `challenge_token_${slug}`;
const unlockKey = (slug) => `challenge_unlocked_${slug}`;

// ─── Code gate screen shown before any challenge content ─────────────────────
const ChallengeCodeGate = ({ slug, challengeInfo, isRTL, onUnlocked }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const titleText = isRTL
    ? (challengeInfo?.title_ar || challengeInfo?.title_en || 'التحدي')
    : (challengeInfo?.title_en || challengeInfo?.title_ar || 'Challenge');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setChecking(true);
    setError('');
    try {
      const res = await fetch(`${backendUrl}/api/challenges/${slug}/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: code.trim() })
      });
      if (res.ok) {
        const { valid } = await res.json();
        if (valid) {
          localStorage.setItem(unlockKey(slug), '1');
          onUnlocked(code.trim());
        } else {
          setError(isRTL ? 'الرمز غير صحيح. حاول مرة أخرى.' : 'Incorrect code. Please try again.');
        }
      } else {
        setError(isRTL ? 'حدث خطأ. حاول مرة أخرى.' : 'Something went wrong. Please try again.');
      }
    } catch {
      setError(isRTL ? 'تعذّر الاتصال. تحقق من اتصالك.' : 'Connection error. Please check your network.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal flex items-center justify-center px-4">
      <div className="w-full max-w-sm text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-gold/30 mb-6">
          <Lock size={28} className="text-gold" />
        </div>
        <p className="text-gold text-xs uppercase tracking-[0.2em] mb-3">
          {isRTL ? 'محتوى حصري' : 'Exclusive Access'}
        </p>
        <h1 className="font-serif text-2xl md:text-3xl text-cream mb-2">{titleText}</h1>
        <p className="text-cream/50 text-sm mb-8">
          {isRTL
            ? 'هذا التحدي متاح للمدعوين فقط. أدخل رمز الوصول للمتابعة.'
            : 'This challenge is available by invitation only. Enter your access code to continue.'}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="relative">
            <Key size={15} className="absolute top-1/2 -translate-y-1/2 left-4 text-gold/50 pointer-events-none" />
            <input
              type="text"
              value={code}
              onChange={e => { setCode(e.target.value); setError(''); }}
              placeholder={isRTL ? 'رمز الوصول' : 'Access code'}
              className="w-full bg-charcoal border border-cream/20 text-cream text-center tracking-widest placeholder:text-cream/30 rounded-lg px-4 pl-10 py-3 focus:outline-none focus:border-gold/60 transition-colors"
              autoComplete="off"
              autoFocus
            />
          </div>
          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}
          <button
            type="submit"
            disabled={checking || !code.trim()}
            className="w-full bg-gold text-charcoal font-semibold py-3 rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {checking
              ? (isRTL ? 'جاري التحقق...' : 'Verifying...')
              : (isRTL ? 'دخول' : 'Enter')}
          </button>
        </form>
      </div>
    </div>
  );
};

// ─── ChallengeProgress ────────────────────────────────────────────────────────
export const ChallengeProgress = ({ effectiveDay, totalDays, progressPercent, userName, isRTL }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <span className="text-cream-300 text-sm">
        {isRTL
          ? `اليوم ${effectiveDay} من ${totalDays}`
          : `Day ${effectiveDay} of ${totalDays}`}
      </span>
      <span className="text-gold text-xs">{progressPercent}%</span>
    </div>
    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
      <div
        className="h-full bg-gold/70 rounded-full transition-all duration-700"
        style={{ width: `${progressPercent}%` }}
      />
    </div>
    {userName && (
      <p className="text-cream-400/50 text-xs mt-2">
        {isRTL
          ? `أهلاً ${userName} — استمري في رحلتك`
          : `Welcome back, ${userName} — keep going`}
      </p>
    )}
  </div>
);

// ─── LockedDayCard ────────────────────────────────────────────────────────────
export const LockedDayCard = ({ day, isRTL }) => (
  <div className="flex items-center gap-4 p-4 rounded-xl border border-white/5 bg-charcoal-100/50 opacity-60">
    <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center flex-shrink-0">
      <Lock size={14} className="text-cream-400/50" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-cream-400/60 text-xs uppercase tracking-wider mb-0.5">
        {isRTL ? `اليوم ${day.day_number}` : `Day ${day.day_number}`}
      </p>
      <p className="text-cream-300/50 text-sm truncate">
        {isRTL ? day.title_ar : day.title_en}
      </p>
    </div>
    <Lock size={13} className="text-cream-400/30 flex-shrink-0" />
  </div>
);

// ─── ChallengeDayCard (past / accessible) ─────────────────────────────────────
export const ChallengeDayCard = ({ day, isRTL, onSelect }) => (
  <button
    onClick={onSelect}
    className="w-full flex items-center gap-4 p-4 rounded-xl border border-gold/10 hover:border-gold/25 bg-charcoal-100/60 hover:bg-charcoal-100 transition-all group text-left"
  >
    <div className="w-10 h-10 rounded-full bg-gold/10 border border-gold/20 flex items-center justify-center flex-shrink-0">
      <CheckCircle size={15} className="text-gold/70" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-gold/60 text-xs uppercase tracking-wider mb-0.5">
        {isRTL ? `اليوم ${day.day_number}` : `Day ${day.day_number}`}
      </p>
      <p className="text-cream-300 text-sm truncate group-hover:text-cream transition-colors">
        {isRTL ? day.title_ar : day.title_en}
      </p>
    </div>
    <ChevronRight size={14} className="text-cream-400/40 group-hover:text-gold transition-colors flex-shrink-0" />
  </button>
);

// ─── Video Player (full-screen overlay) ───────────────────────────────────────
const VideoPlayer = ({ dayNumber, dayData, isRTL, onClose }) => {
  const title = isRTL ? dayData.title_ar : dayData.title_en;
  const desc = isRTL ? dayData.short_description_ar : dayData.short_description_en;

  const getEmbedUrl = (url) => {
    if (!url) return null;
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1`;
    const vm = url.match(/vimeo\.com\/(\d+)/);
    if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
    return url;
  };

  const embedUrl = getEmbedUrl(dayData.video_url);

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/95 flex flex-col overflow-y-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/5">
        <button onClick={onClose} className="flex items-center gap-2 text-cream-400 hover:text-cream transition-colors text-sm">
          <ArrowLeft size={16} />
          {isRTL ? 'العودة' : 'Back'}
        </button>
        <span className="text-gold text-xs uppercase tracking-wider">
          {isRTL ? `اليوم ${dayNumber}` : `Day ${dayNumber}`}
        </span>
        <button onClick={onClose} className="text-cream-400 hover:text-cream transition-colors">
          <X size={20} />
        </button>
      </div>
      <div className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-8">
        <h2 className="font-serif text-2xl sm:text-3xl text-cream mb-3">{title}</h2>
        {desc && <p className="text-cream-300 text-sm leading-relaxed mb-8">{desc}</p>}
        {embedUrl ? (
          <div className="aspect-video rounded-xl overflow-hidden border border-white/10 bg-black">
            <iframe
              src={embedUrl}
              className="w-full h-full"
              allowFullScreen
              allow="autoplay; fullscreen; picture-in-picture"
              title={title}
            />
          </div>
        ) : (
          <div className="aspect-video rounded-xl border border-gold/20 bg-charcoal-100 flex items-center justify-center">
            <div className="text-center">
              <Play size={40} className="text-gold/40 mx-auto mb-3" />
              <p className="text-cream-400 text-sm">
                {isRTL ? 'سيتم إضافة الفيديو قريباً' : 'Video coming soon'}
              </p>
            </div>
          </div>
        )}
        {dayData.duration_text && (
          <p className="text-cream-400/60 text-xs mt-4 flex items-center gap-1">
            <Calendar size={11} />
            {dayData.duration_text}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── FeedbackForm (Engine 6) ──────────────────────────────────────────────────
const StarRating = ({ value, onChange }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="transition-transform hover:scale-110"
      >
        <Star
          size={24}
          className={n <= value ? 'text-gold fill-gold' : 'text-white/20'}
        />
      </button>
    ))}
  </div>
);

const FeedbackForm = ({ slug, enrollmentId, userName, isRTL, onSubmitted }) => {
  const [form, setForm] = useState({
    rating: 0,
    testimonial: '',
    reflection: '',
    nps_score: null,
    permission_to_share: true
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rating === 0) {
      toast.error(isRTL ? 'يرجى اختيار تقييم' : 'Please select a rating');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/challenges/${slug}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enrollment_id: enrollmentId,
          user_name: userName,
          ...form
        })
      });
      if (res.ok) {
        setDone(true);
        toast.success(isRTL ? 'شكراً على تقييمكِ!' : 'Thank you for your feedback!');
        if (onSubmitted) onSubmitted();
      } else {
        const err = await res.json();
        if (res.status === 409) {
          setDone(true);
        } else {
          toast.error(err.detail || 'Submission failed');
        }
      }
    } catch {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  const inpCls = 'w-full bg-charcoal-100 border border-white/10 rounded-lg px-3 py-2 text-cream text-sm focus:border-gold/50 focus:outline-none placeholder:text-cream-400/40 resize-none';
  const lblCls = 'block text-gold text-xs uppercase tracking-wider mb-1.5';

  if (done) {
    return (
      <div className="text-center py-8 border border-gold/15 rounded-2xl">
        <ThumbsUp size={28} className="text-gold mx-auto mb-3" />
        <p className="text-cream font-serif text-lg mb-1">
          {isRTL ? 'تقييمكِ وصل!' : 'Feedback received!'}
        </p>
        <p className="text-cream-400 text-sm">
          {isRTL ? 'شكراً من القلب على وقتكِ.' : 'Thank you from the heart for your time.'}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border border-gold/15 rounded-2xl p-6 space-y-5" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare size={15} className="text-gold" />
        <h3 className="text-cream font-serif text-lg">
          {isRTL ? 'شاركينا تجربتكِ' : 'Share Your Experience'}
        </h3>
      </div>

      <div>
        <label className={lblCls}>{isRTL ? 'تقييمكِ العام' : 'Overall Rating'}</label>
        <StarRating value={form.rating} onChange={v => setForm(f => ({ ...f, rating: v }))} />
      </div>

      <div>
        <label className={lblCls}>
          {isRTL ? 'تقييم NPS (0–10) — ما مدى احتمال توصيتكِ بهذا التحدي؟' : 'How likely are you to recommend this? (0–10)'}
        </label>
        <div className="flex flex-wrap gap-2">
          {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
            <button
              key={n}
              type="button"
              onClick={() => setForm(f => ({ ...f, nps_score: n }))}
              className={`w-9 h-9 rounded-lg text-sm font-medium transition-all border ${
                form.nps_score === n
                  ? 'bg-gold text-charcoal border-gold'
                  : 'border-white/10 text-cream-400 hover:border-gold/40'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className={lblCls}>{isRTL ? 'شهادتكِ (اختياري)' : 'Testimonial (optional)'}</label>
        <textarea
          rows={3}
          className={inpCls}
          placeholder={isRTL ? 'ما الذي تغيّر في حياتكِ بعد هذا التحدي؟' : 'What changed in your life after this challenge?'}
          value={form.testimonial}
          onChange={e => setForm(f => ({ ...f, testimonial: e.target.value }))}
        />
      </div>

      <div>
        <label className={lblCls}>{isRTL ? 'تأمّلكِ الشخصي (خاص ومحمي)' : 'Personal Reflection (private)'}</label>
        <textarea
          rows={2}
          className={inpCls}
          placeholder={isRTL ? 'ملاحظات لنفسكِ فقط...' : 'Notes just for yourself...'}
          value={form.reflection}
          onChange={e => setForm(f => ({ ...f, reflection: e.target.value }))}
        />
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={form.permission_to_share}
          onChange={e => setForm(f => ({ ...f, permission_to_share: e.target.checked }))}
          className="accent-gold w-4 h-4 mt-0.5 flex-shrink-0"
        />
        <span className="text-cream-300 text-xs leading-relaxed">
          {isRTL
            ? 'أوافق على مشاركة شهادتي (باسمي الأول فقط) على موقع منى بيزاري'
            : 'I allow my testimonial (first name only) to be shared on Mona Bizzari\'s website'}
        </span>
      </label>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-gold text-charcoal text-sm font-medium uppercase tracking-wider rounded-lg hover:bg-gold/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {loading ? <Loader2 size={15} className="animate-spin" /> : <ThumbsUp size={14} />}
        {isRTL ? 'أرسلي تقييمكِ' : 'Submit Feedback'}
      </button>
    </form>
  );
};

// ─── UpsellSection (Engine 7) ──────────────────────────────────────────────────
const UpsellSection = ({ slug, isRTL }) => {
  const [actions, setActions] = useState([]);

  useEffect(() => {
    fetch(`${backendUrl}/api/challenges/${slug}/upsell`)
      .then(r => r.json())
      .then(d => Array.isArray(d) ? setActions(d) : null)
      .catch(() => {});
  }, [slug]);

  const defaultActions = isRTL ? [
    { label_en: 'تواصلي مع منى', label_ar: 'تواصلي مع منى', url: '/#connect', style: 'primary' },
    { label_en: 'استكشفي البرامج', label_ar: 'استكشفي البرامج', url: '/#programs', style: 'outline' }
  ] : [
    { label_en: 'Connect with Mona', label_ar: 'Connect with Mona', url: '/#connect', style: 'primary' },
    { label_en: 'Explore Programs', label_ar: 'Explore Programs', url: '/#programs', style: 'outline' }
  ];

  const displayActions = actions.length > 0 ? actions : defaultActions;

  return (
    <div className="space-y-3">
      {displayActions.length > 0 && (
        <p className="text-cream-400 text-xs uppercase tracking-wider flex items-center gap-1.5">
          <Gift size={11} />
          {isRTL ? 'الخطوة التالية' : 'What\'s Next'}
        </p>
      )}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        {displayActions.map((a, i) => {
          const label = isRTL ? (a.label_ar || a.label_en) : (a.label_en || a.label_ar);
          const isExternal = a.url && a.url.startsWith('http');
          const btnBase = 'flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium uppercase tracking-wider rounded-lg transition-all';
          const btnStyle = (a.style === 'outline')
            ? `${btnBase} border border-gold/50 text-gold hover:bg-gold hover:text-charcoal`
            : `${btnBase} bg-gold text-charcoal hover:bg-gold/90`;
          return isExternal
            ? (
              <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" className={btnStyle}>
                {label}
                <ExternalLink size={12} />
              </a>
            ) : (
              <Link key={i} to={a.url || '/'} className={btnStyle}>
                {label}
              </Link>
            );
        })}
      </div>
    </div>
  );
};

// ─── ChallengeCompletion ──────────────────────────────────────────────────────
export const ChallengeCompletion = ({ slug, enrollmentId, userName, totalDays, challengeTitle, isRTL }) => {
  const [showFeedback, setShowFeedback] = useState(true);

  return (
    <div className="min-h-screen bg-charcoal px-4 py-16" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-lg mx-auto space-y-10">
        {/* Trophy header */}
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-8">
            <Award size={36} className="text-gold" />
          </div>
          <p className="text-gold text-xs uppercase tracking-[0.2em] mb-4">
            {isRTL ? 'تهانينا' : 'Congratulations'}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-cream mb-6">
            {isRTL
              ? `أكملتِ ${challengeTitle}، ${userName || ''}! 🌟`
              : `You completed ${challengeTitle}, ${userName || ''}! 🌟`}
          </h1>
          <p className="text-cream-300 text-sm sm:text-base leading-relaxed">
            {isRTL
              ? `لقد أتممتِ الـ ${totalDays} يوماً بكل شجاعة وانتظام. هذا ليس نهاية — بل بداية نسخة جديدة منكِ.`
              : `You showed up for ${totalDays} days with courage and consistency. This is not an end — it is the beginning of a new chapter.`}
          </p>
        </div>

        {/* Upsell CTAs */}
        <UpsellSection slug={slug} isRTL={isRTL} />

        {/* Feedback form */}
        {showFeedback && enrollmentId && (
          <FeedbackForm
            slug={slug}
            enrollmentId={enrollmentId}
            userName={userName}
            isRTL={isRTL}
            onSubmitted={() => setShowFeedback(false)}
          />
        )}
      </div>
    </div>
  );
};

// ─── Enrollment Form ──────────────────────────────────────────────────────────
const EnrollmentForm = ({ slug, challengeInfo, isRTL, onEnrolled, onBack, verifiedCode }) => {
  const requiresCode = !!challengeInfo?.requires_code;
  const [form, setForm] = useState({ user_name: '', user_email: '', phone: '', access_code: verifiedCode || '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const title = isRTL
    ? (challengeInfo?.title_ar || challengeInfo?.title_en || '')
    : (challengeInfo?.title_en || '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // Validate phone — must have at least 7 digits
      const phoneDigits = (form.phone || '').replace(/\D/g, '');
      if (phoneDigits.length < 7) {
        setError(isRTL ? 'يرجى إدخال رقم هاتف صحيح' : 'Please enter a valid phone number');
        setLoading(false);
        return;
      }
      const payload = { ...form, challenge_slug: slug };
      if (!requiresCode) delete payload.access_code;
      const res = await fetch(`${backendUrl}/api/challenges/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Enrollment failed');
      localStorage.setItem(tokenKey(slug), data.access_token);
      toast.success(isRTL ? 'تم التسجيل بنجاح! رحلتك تبدأ اليوم.' : 'Enrolled! Your journey begins today.');
      onEnrolled(data.access_token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const labelClass = 'block text-gold text-xs uppercase tracking-wider mb-1.5';
  const inputClass = 'w-full px-4 py-3 bg-charcoal-100 border border-white/10 text-cream text-sm placeholder:text-cream-400/40 focus:border-gold focus:outline-none transition-colors rounded-lg';

  return (
    <div className="min-h-screen bg-charcoal flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <button onClick={onBack} className="text-cream-400 hover:text-cream text-sm flex items-center gap-1.5 mx-auto mb-6 transition-colors">
            <ArrowLeft size={13} />
            {isRTL ? 'العودة' : 'Back'}
          </button>
          <p className="text-gold text-xs uppercase tracking-[0.2em] mb-3">
            {isRTL ? 'سجّلي الآن' : 'Get Started'}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-cream mb-4">{title}</h1>
          <p className="text-cream-300 text-sm leading-relaxed max-w-sm mx-auto">
            {isRTL
              ? 'أدخلي بياناتك للانضمام إلى التحدّي والحصول على وصول فوري.'
              : 'Enter your details to join the challenge and get instant access.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="border border-gold/15 rounded-2xl p-7 space-y-5" dir={isRTL ? 'rtl' : 'ltr'}>
          <div>
            <label className={labelClass}>
              <User size={11} className="inline mr-1" />
              {isRTL ? 'الاسم الكريم' : 'Your Name'}
            </label>
            <input
              type="text"
              required
              placeholder={isRTL ? 'اكتبي اسمك هنا' : 'Enter your name'}
              value={form.user_name}
              onChange={e => setForm(f => ({ ...f, user_name: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              <Mail size={11} className="inline mr-1" />
              {isRTL ? 'البريد الإلكتروني' : 'Email Address'}
            </label>
            <input
              type="email"
              required
              placeholder={isRTL ? 'بريدك الإلكتروني' : 'your@email.com'}
              value={form.user_email}
              onChange={e => setForm(f => ({ ...f, user_email: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              <Phone size={11} className="inline mr-1" />
              {isRTL ? 'رقم الهاتف *' : 'Phone Number *'}
            </label>
            <input
              type="tel"
              required
              placeholder={isRTL ? '+20 1XX XXX XXXX' : '+1 (555) 000-0000'}
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              className={inputClass}
            />
          </div>

          {requiresCode && !verifiedCode && (
            <div>
              <label className={labelClass}>
                <Key size={11} className="inline mr-1" />
                {isRTL ? 'رمز الوصول' : 'Access Code'}
              </label>
              <input
                type="text"
                required
                placeholder={isRTL ? 'أدخلي الكود الخاص بكِ' : 'Enter your access code'}
                value={form.access_code}
                onChange={e => setForm(f => ({ ...f, access_code: e.target.value }))}
                className={inputClass}
              />
            </div>
          )}

          {error && (
            <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gold text-charcoal text-sm font-medium uppercase tracking-wider rounded-lg hover:bg-gold/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Play size={15} />}
            {isRTL ? 'ابدئي رحلتك الآن' : 'Begin My Journey'}
          </button>

          <p className="text-cream-400/50 text-xs text-center">
            {isRTL
              ? 'إذا كنتِ مسجّلة بالفعل، استخدمي نفس البريد الإلكتروني للمتابعة.'
              : 'Already enrolled? Use the same email to continue from where you left off.'}
          </p>
        </form>
      </div>
    </div>
  );
};

// ─── Challenge Landing (pre-enrollment) ───────────────────────────────────────
const ChallengeLanding = ({ slug, challengeInfo, isRTL, onEnroll }) => {
  const totalDays = challengeInfo?.total_days || '?';
  const titleEN = challengeInfo?.title_en || 'Challenge';
  const titleAR = challengeInfo?.title_ar || challengeInfo?.title_en || 'تحدّي';
  const descEN = challengeInfo?.description_en || '';
  const descAR = challengeInfo?.description_ar || challengeInfo?.description_en || '';

  const title = isRTL ? titleAR : titleEN;
  const desc = isRTL ? descAR : descEN;

  const benefits = isRTL ? [
    `${totalDays} فيديو قصير ومركّز — يوماً بيوم`,
    'تحوّل حقيقي في طريقة تفكيركِ وشعوركِ',
    'محتوى يُفتح يوماً بيوم — بوتيرتكِ أنتِ',
    'دعم من منى بيزاري شخصياً'
  ] : [
    `${totalDays} focused short videos — one per day`,
    'Real transformation in how you think and feel',
    'Content unlocks day by day — at your own pace',
    'Personal guidance from Mona Bizzari'
  ];

  return (
    <div className="min-h-screen bg-charcoal" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="px-6 py-4 border-b border-white/5">
        <Link to="/" className="text-cream-400 hover:text-cream text-sm flex items-center gap-2 w-fit transition-colors">
          <ArrowLeft size={14} />
          {isRTL ? 'العودة للرئيسية' : 'Back to Home'}
        </Link>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 space-y-12">
        <div className="text-center">
          <p className="text-gold text-xs uppercase tracking-[0.2em] mb-4">
            {isRTL ? 'برنامج حصري' : 'Exclusive Program'}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl text-cream mb-6 leading-tight">{title}</h1>
          {desc && (
            <p className="text-cream-300 text-base sm:text-lg leading-relaxed max-w-lg mx-auto">{desc}</p>
          )}
        </div>

        {/* Day count badge — dynamic */}
        <div className="flex justify-center">
          <div className="border border-gold/20 bg-gold/5 rounded-2xl px-10 py-8 text-center">
            <span className="font-serif text-6xl text-gold">{totalDays}</span>
            <p className="text-cream-300 text-sm mt-1 uppercase tracking-wider">
              {isRTL
                ? `${totalDays === 1 ? 'يوم' : 'يوم'} · فيديو · لحظة تحوّل`
                : `${totalDays === 1 ? 'Day' : 'Days'} · Videos · Moments of change`}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {benefits.map((b, i) => (
            <div key={i} className="flex items-start gap-3 p-4 rounded-xl border border-gold/10 bg-charcoal-100/50">
              <CheckCircle size={15} className="text-gold mt-0.5 flex-shrink-0" />
              <span className="text-cream-300 text-sm">{b}</span>
            </div>
          ))}
        </div>

        <div className="text-center">
          <button
            onClick={onEnroll}
            className="px-10 py-4 bg-gold text-charcoal text-sm font-medium uppercase tracking-wider rounded-xl hover:bg-gold/90 transition-all flex items-center gap-2 mx-auto"
          >
            <Play size={16} />
            {isRTL ? 'ابدئي التحدّي الآن' : 'Start the Challenge Now'}
          </button>
          <p className="text-cream-400/40 text-xs mt-4">
            {isRTL ? 'فقط سجّلي اسمكِ وبريدكِ' : 'Just enter your name and email'}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Challenge Dashboard (enrolled user) ─────────────────────────────────────
const ChallengeDashboard = ({ slug, token, isRTL }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeDay, setActiveDay] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [loadingDay, setLoadingDay] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/challenges/${slug}/me?token=${encodeURIComponent(token)}`);
      if (!res.ok) {
        const err = await res.json();
        setError(err.detail || 'Failed to load challenge');
        return;
      }
      setData(await res.json());
    } catch {
      setError('Could not connect to the server. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [slug, token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const openDay = async (dayNumber) => {
    setLoadingDay(true);
    setActiveDay(dayNumber);
    try {
      const res = await fetch(`${backendUrl}/api/challenges/${slug}/day/${dayNumber}?token=${encodeURIComponent(token)}`);
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Could not load video');
        setActiveDay(null);
        return;
      }
      setDayData(await res.json());
    } catch {
      toast.error('Failed to load video');
      setActiveDay(null);
    } finally {
      setLoadingDay(false);
    }
  };

  const closeVideo = () => { setActiveDay(null); setDayData(null); };

  if (loading) return (
    <div className="min-h-screen bg-charcoal flex items-center justify-center">
      <Loader2 size={32} className="animate-spin text-gold" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-charcoal flex items-center justify-center px-4">
      <div className="text-center max-w-sm">
        <p className="text-red-400 mb-4">{error}</p>
        <button
          onClick={() => { localStorage.removeItem(tokenKey(slug)); window.location.reload(); }}
          className="text-gold text-sm underline"
        >
          {isRTL ? 'تسجيل من جديد' : 'Re-enroll'}
        </button>
      </div>
    </div>
  );

  if (!data) return null;

  const { enrollment, challenge, progress, days } = data;
  const { effective_day, total_days, progress_percent, is_completed } = progress;
  const challengeTitle = isRTL ? challenge.title_ar : challenge.title_en;

  if (is_completed && effective_day >= total_days) {
    return (
      <ChallengeCompletion
        slug={slug}
        enrollmentId={enrollment.id}
        userName={enrollment.user_name}
        totalDays={total_days}
        challengeTitle={challengeTitle}
        isRTL={isRTL}
      />
    );
  }

  const currentDayObj = days.find(d => d.is_current);
  const prevDays = days.filter(d => !d.is_current && d.is_accessible && !d.is_locked);
  const futureDays = days.filter(d => d.is_locked);

  return (
    <div className="min-h-screen bg-charcoal" dir={isRTL ? 'rtl' : 'ltr'}>
      {activeDay && dayData && (
        <VideoPlayer dayNumber={activeDay} dayData={dayData} isRTL={isRTL} onClose={closeVideo} />
      )}
      {loadingDay && (
        <div className="fixed inset-0 z-50 bg-charcoal/80 flex items-center justify-center">
          <Loader2 size={36} className="animate-spin text-gold" />
        </div>
      )}

      {/* Header */}
      <div className="border-b border-white/5 px-4 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="text-cream-400 hover:text-cream transition-colors flex items-center gap-2 text-sm">
          <ArrowLeft size={15} />
          {isRTL ? 'الرئيسية' : 'Home'}
        </Link>
        <span className="text-gold text-xs uppercase tracking-[0.15em]">{challengeTitle}</span>
        <div className="w-20" />
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Progress bar */}
        <ChallengeProgress
          effectiveDay={effective_day}
          totalDays={total_days}
          progressPercent={progress_percent}
          userName={enrollment.user_name}
          isRTL={isRTL}
        />

        {/* Current day hero card */}
        {currentDayObj && (
          <div className="border border-gold/20 bg-charcoal-100 rounded-2xl overflow-hidden">
            <div className="px-6 pt-6 pb-2">
              <p className="text-gold text-xs uppercase tracking-[0.2em] mb-1 flex items-center gap-1.5">
                <BarChart2 size={11} />
                {isRTL ? 'يومكِ الآن' : "Today's Video"}
              </p>
              <h2 className="font-serif text-xl sm:text-2xl text-cream mb-2">
                {isRTL ? currentDayObj.title_ar : currentDayObj.title_en}
              </h2>
              {(isRTL ? currentDayObj.short_description_ar : currentDayObj.short_description_en) && (
                <p className="text-cream-300 text-sm leading-relaxed mb-4">
                  {isRTL ? currentDayObj.short_description_ar : currentDayObj.short_description_en}
                </p>
              )}
            </div>
            <div className="px-6 pb-6">
              <button
                onClick={() => openDay(currentDayObj.day_number)}
                className="w-full aspect-video rounded-xl bg-charcoal border border-gold/10 hover:border-gold/30 flex items-center justify-center group transition-all relative overflow-hidden"
              >
                {currentDayObj.thumbnail_url && (
                  <img
                    src={currentDayObj.thumbnail_url.startsWith('/') ? `${backendUrl}${currentDayObj.thumbnail_url}` : currentDayObj.thumbnail_url}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-60 transition-opacity"
                  />
                )}
                <div className="relative z-10 w-14 h-14 rounded-full bg-gold/90 group-hover:bg-gold flex items-center justify-center transition-all shadow-lg">
                  <Play size={22} className="text-charcoal ml-0.5" />
                </div>
              </button>
              {currentDayObj.duration_text && (
                <p className="text-cream-400/50 text-xs mt-3 flex items-center gap-1">
                  <Calendar size={11} />
                  {currentDayObj.duration_text}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Previous days */}
        {prevDays.length > 0 && (
          <div>
            <h3 className="text-cream-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
              <CheckCircle size={12} className="text-gold/60" />
              {isRTL ? 'الأيام السابقة' : 'Previous Days'}
            </h3>
            <div className="space-y-2">
              {prevDays.slice().reverse().map(day => (
                <ChallengeDayCard key={day.day_number} day={day} isRTL={isRTL} onSelect={() => openDay(day.day_number)} />
              ))}
            </div>
          </div>
        )}

        {/* Future locked days */}
        {futureDays.length > 0 && (
          <div>
            <h3 className="text-cream-400/50 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
              <Lock size={12} className="text-cream-400/40" />
              {isRTL ? 'الأيام القادمة' : 'Coming Days'}
            </h3>
            <div className="space-y-2">
              {futureDays.slice(0, 5).map(day => (
                <LockedDayCard key={day.day_number} day={day} isRTL={isRTL} />
              ))}
              {futureDays.length > 5 && (
                <p className="text-cream-400/40 text-xs text-center py-2">
                  {isRTL
                    ? `+ ${futureDays.length - 5} أيام أخرى قادمة`
                    : `+ ${futureDays.length - 5} more days ahead`}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center border-t border-white/5 pt-8 pb-4">
          <p className="text-cream-400/50 text-xs italic">
            {isRTL
              ? '"كل يوم خطوة أقرب إلى نسخة أفضل منكِ"'
              : '"Every day is one step closer to your best self"'}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Root: reads slug from URL, routes to correct view ───────────────────────
const ChallengePage = () => {
  const { slug } = useParams();
  const { isRTL } = useLanguage();
  const [view, setView] = useState('loading'); // loading | gate | landing | enroll | dashboard
  const [token, setToken] = useState(null);
  const [challengeInfo, setChallengeInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [verifiedCode, setVerifiedCode] = useState('');

  // Load challenge metadata; gate decision happens here once both
  // the info and the stored-token check have completed.
  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        const res = await fetch(`${backendUrl}/api/challenges/${slug}`);
        if (res.ok) {
          const info = await res.json();
          setChallengeInfo(info);

          const storedToken  = localStorage.getItem(tokenKey(slug));
          const alreadyUnlocked = localStorage.getItem(unlockKey(slug));

          if (storedToken) {
            setToken(storedToken);
            setView('dashboard');
          } else if (info.requires_code && !alreadyUnlocked) {
            setView('gate');
          } else {
            setView('landing');
          }
        } else {
          setNotFound(true);
        }
      } catch {
        setNotFound(true);
      } finally {
        setInfoLoading(false);
      }
    })();
  }, [slug]);

  const handleEnrolled = (newToken) => {
    setToken(newToken);
    setView('dashboard');
  };

  if (infoLoading || view === 'loading') {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-gold" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-gold text-xs uppercase tracking-[0.2em] mb-4">Not Found</p>
          <h1 className="font-serif text-3xl text-cream mb-6">This challenge is not available.</h1>
          <Link to="/" className="text-gold text-sm underline">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {view === 'gate' && (
        <ChallengeCodeGate
          slug={slug}
          challengeInfo={challengeInfo}
          isRTL={isRTL}
          onUnlocked={(code) => { setVerifiedCode(code); setView('landing'); }}
        />
      )}
      {view === 'dashboard' && token && (
        <ChallengeDashboard slug={slug} token={token} isRTL={isRTL} />
      )}
      {view === 'enroll' && (
        <EnrollmentForm
          slug={slug}
          challengeInfo={challengeInfo}
          isRTL={isRTL}
          onEnrolled={handleEnrolled}
          onBack={() => setView('landing')}
          verifiedCode={verifiedCode}
        />
      )}
      {view === 'landing' && (
        <ChallengeLanding
          slug={slug}
          challengeInfo={challengeInfo}
          isRTL={isRTL}
          onEnroll={() => setView('enroll')}
        />
      )}
      <Toaster position="top-center" />
    </>
  );
};

export default ChallengePage;
